import React, { useState } from 'react';
import { submitEnquiry } from '../data/mock';
import { toast } from 'sonner';

const EnquiryForm = () => {
  const [formData, setFormData] = useState({
    name: '',
    designation: '',
    email: '',
    companyName: '',
    contactNumber: '',
    enquiryDetails: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Basic validation
    if (!formData.name || !formData.email || !formData.companyName || !formData.contactNumber) {
      toast.error('Please fill in all required fields');
      return;
    }

    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      toast.error('Please enter a valid email address');
      return;
    }

    setIsSubmitting(true);
    
    try {
      const response = await submitEnquiry(formData);
      if (response.success) {
        toast.success(response.message);
        // Reset form
        setFormData({
          name: '',
          designation: '',
          email: '',
          companyName: '',
          contactNumber: '',
          enquiryDetails: ''
        });
      }
    } catch (error) {
      toast.error('Something went wrong. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contact" className="bg-black py-24 md:py-32 px-[7.6923%]">
      <div className="max-w-[1400px] mx-auto">
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-semibold text-white mb-4 tracking-tight">
            Get In Touch
          </h2>
          <div className="w-20 h-1 bg-[#00FFD1] mx-auto mb-6"></div>
          <p className="text-lg text-white/85 max-w-[700px] mx-auto">
            Ready to transform your business? Share your requirements and our team will reach out to you
          </p>
        </div>

        <div className="max-w-[800px] mx-auto bg-[#121212] p-8 md:p-12 border border-white/25">
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Name */}
            <div>
              <label htmlFor="name" className="block text-white text-lg font-medium mb-2">
                Name <span className="text-[#00FFD1]">*</span>
              </label>
              <input
                type="text"
                id="name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                required
                className="w-full bg-black border border-white/25 text-white px-4 py-3 text-base focus:outline-none focus:border-[#00FFD1] transition-colors duration-300"
                placeholder="Enter your full name"
              />
            </div>

            {/* Designation */}
            <div>
              <label htmlFor="designation" className="block text-white text-lg font-medium mb-2">
                Designation
              </label>
              <input
                type="text"
                id="designation"
                name="designation"
                value={formData.designation}
                onChange={handleChange}
                className="w-full bg-black border border-white/25 text-white px-4 py-3 text-base focus:outline-none focus:border-[#00FFD1] transition-colors duration-300"
                placeholder="Your role or position"
              />
            </div>

            {/* Email and Company Name - Grid */}
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <label htmlFor="email" className="block text-white text-lg font-medium mb-2">
                  Email <span className="text-[#00FFD1]">*</span>
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="w-full bg-black border border-white/25 text-white px-4 py-3 text-base focus:outline-none focus:border-[#00FFD1] transition-colors duration-300"
                  placeholder="your.email@company.com"
                />
              </div>

              <div>
                <label htmlFor="companyName" className="block text-white text-lg font-medium mb-2">
                  Company Name <span className="text-[#00FFD1]">*</span>
                </label>
                <input
                  type="text"
                  id="companyName"
                  name="companyName"
                  value={formData.companyName}
                  onChange={handleChange}
                  required
                  className="w-full bg-black border border-white/25 text-white px-4 py-3 text-base focus:outline-none focus:border-[#00FFD1] transition-colors duration-300"
                  placeholder="Your company name"
                />
              </div>
            </div>

            {/* Contact Number */}
            <div>
              <label htmlFor="contactNumber" className="block text-white text-lg font-medium mb-2">
                Contact Number <span className="text-[#00FFD1]">*</span>
              </label>
              <input
                type="tel"
                id="contactNumber"
                name="contactNumber"
                value={formData.contactNumber}
                onChange={handleChange}
                required
                className="w-full bg-black border border-white/25 text-white px-4 py-3 text-base focus:outline-none focus:border-[#00FFD1] transition-colors duration-300"
                placeholder="+91 XXXXX XXXXX"
              />
            </div>

            {/* Enquiry Details */}
            <div>
              <label htmlFor="enquiryDetails" className="block text-white text-lg font-medium mb-2">
                Enquiry Details
              </label>
              <textarea
                id="enquiryDetails"
                name="enquiryDetails"
                value={formData.enquiryDetails}
                onChange={handleChange}
                rows="6"
                maxLength="500"
                className="w-full bg-black border border-white/25 text-white px-4 py-3 text-base focus:outline-none focus:border-[#00FFD1] transition-colors duration-300 resize-none"
                placeholder="Tell us about your requirements (max 500 characters)"
              />
              <div className="text-right text-sm text-white/60 mt-2">
                {formData.enquiryDetails.length}/500 characters
              </div>
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full bg-[#00FFD1] text-black border-none px-8 py-4 text-lg font-medium cursor-pointer transition-all duration-400 hover:bg-[rgba(0,255,209,0.1)] hover:text-[#00FFD1] disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSubmitting ? 'Submitting...' : 'Submit Enquiry'}
            </button>
          </form>
        </div>
      </div>
    </section>
  );
};

export default EnquiryForm;
